if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdSelect() {
        return this.querySelector('[name="id"]');
      }

      connectedCallback() {
        this.events?.abort();
        this.events = new AbortController();
        this.mainProductInfo = Array.from(document.querySelectorAll('product-info'))
          .find((info) => info.dataset.section === this.dataset.section);
        this.productFormActions = this.mainProductInfo?.querySelector('.main-product-form');
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();
        this.select = this.querySelector('select');

        // Do not display a default size while the main picker still requires a choice.
        const sizeGroup = this.mainProductInfo?.querySelector('[data-required-size-option]');
        if (sizeGroup?.querySelector('[data-required-size-input]')) {
          if (!this.select.querySelector('option[value=""]')) {
            const placeholder = new Option('Kies je maat', '');
            placeholder.disabled = true;
            this.select.prepend(placeholder);
          }
          this.select.required = true;
          if (!sizeGroup.querySelector('[data-required-size-input]:checked')) this.select.value = '';
        }

        this.init();
        this.select.addEventListener('change', () => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.variantIdSelect.value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (!this.currentVariant) {
            this.updateButton(true);
            this.isUpdating = false;
            return;
          }

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  element.value = variantOptionVal;
                  const selectedOption = Array.from(options).find((option) => option.value === variantOptionVal);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            // Register this explicit dropdown choice before the main size validator runs.
            this.dispatchEvent(new CustomEvent('stretchshop:sticky-size-selected', { bubbles: true }));
            // Keep the picker as the event target: Hyper must not focus a size radio
            // above the viewport after a choice made in the sticky bar.
            this.mainVariantSelects.dispatchEvent(new Event('change', { bubbles: true }));
          }

          this.isUpdating = false;

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        }, { signal: this.events.signal });
      }

      disconnectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.visibilityFrame);
        if (this.mobileQuery) this.mobileQuery.onchange = null;
        this.variantUnsubscriber?.();
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.remove('sticky-atc-bar--show');
          return;
        }
        this.productId = this.dataset.productId;

        this.mobileQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.mobileQuery.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const buyButton = this.productFormActions.querySelector('[name="add"]');
        this.observer = new IntersectionObserver(
          () => this.updateVisibility(),
          { threshold: 0 }
        );
        this.observer.observe(buyButton || this.productFormActions);
        // Scroll jumps can skip the observer's intersecting state entirely.
        const scheduleVisibility = () => {
          cancelAnimationFrame(this.visibilityFrame);
          this.visibilityFrame = requestAnimationFrame(() => this.updateVisibility());
        };
        window.addEventListener('scroll', scheduleVisibility, { passive: true, signal: this.events.signal });
        window.addEventListener('resize', scheduleVisibility, { signal: this.events.signal });
        window.addEventListener('pageshow', scheduleVisibility, { signal: this.events.signal });
        this.updateVisibility();
        this.syncWithMainProductForm();
      }

      updateVisibility() {
        // Re-read the button so a refreshed product form cannot leave a stale target.
        const buyButton = this.mainProductInfo?.querySelector('.main-product-form [name="add"]');
        const bounds = buyButton?.getBoundingClientRect();
        const hasPassedButton = !!bounds && bounds.height > 0 && bounds.bottom <= 0;
        this.container.classList.toggle('sticky-atc-bar--show', hasPassedButton);
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        this.variantUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant?.id || '';
          if (variant) this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
